import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatBRL } from "@/lib/api";

const EASE = [0.25, 1, 0.5, 1];

export default function CartDrawer() {
  const { items, updateQty, removeItem, total, lineKey, drawerOpen, setDrawerOpen, count } = useCart();
  const navigate = useNavigate();

  const go = (path) => { setDrawerOpen(false); navigate(path); };

  return (
    <AnimatePresence>
      {drawerOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }} onClick={() => setDrawerOpen(false)}
            className="fixed inset-0 bg-[#121212]/40 backdrop-blur-[2px] z-[60]" data-testid="cart-overlay" />
          <motion.aside initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ duration: 0.6, ease: EASE }}
            className="fixed top-0 right-0 h-full w-full max-w-[440px] bg-[#FFFFFF] z-[70] flex flex-col"
            data-testid="cart-drawer">
            <div className="flex items-center justify-between px-7 py-6 border-b border-[#E6E5E1]">
              <span className="text-[11px] uppercase tracking-[0.2em]">Sacola ({count})</span>
              <button data-testid="close-drawer" onClick={() => setDrawerOpen(false)} className="text-[#1A1A1A] hover:text-[#B89C65] transition-colors">
                <X size={20} strokeWidth={1.5} />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
                <ShoppingBag size={40} strokeWidth={1} className="text-[#1A1A1A]/20" />
                <p className="font-serif text-2xl mt-5 text-[#1A1A1A]">Sua sacola está vazia</p>
                <button onClick={() => go("/loja")} className="mt-6 bg-[#1A1A1A] text-[#FFFFFF] px-8 py-4 text-[11px] uppercase tracking-[0.15em] hover:bg-[#B89C65] transition-colors">
                  Explorar a loja
                </button>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-auto no-scrollbar px-7">
                  {items.map((item) => {
                    const k = lineKey(item);
                    return (
                      <div key={k} data-testid={`drawer-item-${item.product_id}`} className="flex gap-4 py-6 border-b border-[#E6E5E1]">
                        <div className="w-20 h-26 bg-[#EFEEEA] overflow-hidden flex-shrink-0" style={{ height: "6.5rem" }}>
                          <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 flex flex-col">
                          <div className="flex justify-between gap-3">
                            <h4 className="font-serif text-lg leading-tight text-[#1A1A1A]">{item.name}</h4>
                            <button onClick={() => removeItem(k)} className="text-[#5C5B59] hover:text-[#1A1A1A] transition-colors h-fit"><X size={15} /></button>
                          </div>
                          <p className="text-[11px] uppercase tracking-[0.1em] text-[#5C5B59] mt-1">{[item.color, item.size].filter(Boolean).join(" · ")}</p>
                          <div className="flex items-center justify-between mt-auto pt-3">
                            <div className="flex items-center border border-[#E6E5E1]">
                              <button onClick={() => updateQty(k, item.quantity - 1)} className="p-2 hover:text-[#B89C65] transition-colors"><Minus size={13} /></button>
                              <span className="w-8 text-center text-xs">{item.quantity}</span>
                              <button onClick={() => updateQty(k, item.quantity + 1)} className="p-2 hover:text-[#B89C65] transition-colors"><Plus size={13} /></button>
                            </div>
                            <span className="text-sm">{formatBRL(item.price * item.quantity)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="border-t border-[#E6E5E1] px-7 py-6">
                  <div className="flex justify-between mb-1">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-[#5C5B59]">Total</span>
                    <span className="text-lg">{formatBRL(total)}</span>
                  </div>
                  <p className="text-[11px] text-[#5C5B59] mb-5">Frete e impostos calculados no checkout.</p>
                  <button data-testid="drawer-checkout" onClick={() => go("/checkout")}
                    className="w-full bg-[#1A1A1A] text-[#FFFFFF] py-4 text-[11px] uppercase tracking-[0.15em] hover:bg-[#B89C65] transition-colors">
                    Finalizar compra
                  </button>
                  <button onClick={() => go("/carrinho")} className="w-full mt-3 text-[11px] uppercase tracking-[0.15em] text-[#5C5B59] hover:text-[#1A1A1A] transition-colors">
                    Ver sacola
                  </button>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
